import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { useEffect, useState, lazy, Suspense, type ComponentType, type ReactNode } from "react";

// Imported as a route stylesheet (not `?url`) so TanStack Start can inline the
// critical CSS into the SSR response instead of emitting a render-blocking
// <link>, which was ~87% of the homepage LCP time.
import "../styles.css";

import { reportLovableError } from "../lib/lovable-error-reporting";
import { SiteHeader, SiteFooter } from "../components/SiteHeader";
import { ScrollPager } from "../components/ScrollPager";
import { WebVitals } from "../components/WebVitals";

import { BackButton } from "../components/BackButton";
import { Breadcrumbs } from "../components/Breadcrumbs";

/**
 * These widgets are optional chrome. If their chunk fails to load (stale hash
 * after a deploy, flaky network, dev-server reload) we must not let the
 * rejection reach the route error boundary and blank the whole page.
 */
function optional<T extends ComponentType<any>>(load: () => Promise<{ default: T }>) {
  return lazy(() => load().catch(() => ({ default: (() => null) as unknown as T })));
}

const CookieBanner = optional(() => import("../components/CookieBanner").then((m) => ({ default: m.CookieBanner })));
const AccessibilityControls = optional(() =>
  import("../components/AccessibilityControls").then((m) => ({ default: m.AccessibilityControls })),
);



function NotFoundComponent() {
  return (
    <div className="mx-auto max-w-3xl px-4 sm:px-6 py-16">
      <p className="text-sm font-semibold text-primary">404</p>
      <h1 className="mt-2 text-3xl sm:text-4xl font-bold">This page doesn't exist</h1>
      <p className="aeo-answer mt-3 text-muted-foreground">
        The URL you opened isn't part of the Pal Stars Palworld wiki — it may have been renamed or
        mistyped. Use the links below, or press ⌘K / Ctrl+K to search Pals, cards and guides.
      </p>
      <div className="mt-8 grid gap-3 sm:grid-cols-2">
        {[
          { to: "/pals", label: "Pal database", desc: "Every Palworld Pal with stats, moves and spawns" },
          { to: "/cards", label: "TCG card database", desc: "Official Palworld card list with printed card numbers" },
          { to: "/guides", label: "Guides", desc: "Breeding, farming, mounts, TCG strategy and more" },
          { to: "/breeding", label: "Breeding calculator", desc: "Combos and probabilities" },
          { to: "/mounts", label: "Mount speeds", desc: "Ground, water and flying mount tables" },
          { to: "/tcg-tools", label: "TCG tools", desc: "Pull rate calculator and pack tracker" },
        ].map((s) => (
          <Link key={s.to} to={s.to as never} className="glass rounded-xl p-4 transition-colors hover:bg-card/70">
            <span className="font-semibold">{s.label}</span>
            <span className="mt-1 block text-sm text-muted-foreground">{s.desc}</span>
          </Link>
        ))}
      </div>
      <div className="mt-8">
        <Link
          to="/"
          className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:brightness-110"
        >
          Back to home
        </Link>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight">This page didn't load</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong. Try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => { router.invalidate(); reset(); }}
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:brightness-110"
          >Try again</button>
          <a href="/" className="rounded-md border px-4 py-2 text-sm font-medium hover:bg-card">Go home</a>
        </div>
      </div>
    </div>
  );
}





export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      // Verifies sc-domain:palstars.com. The https://palstars.com/ URL-prefix
      // property (required by Google's disavow tool, which rejects domain
      // properties) is verified by the FILE token in /public instead: two meta
      // tags with the same name would be deduped by the head merger.
      { name: "google-site-verification", content: "jopC2yq-Negsi4O7hxzbbpSRLugOq1F6iZ-WBMSSI-Y" },
      // No sitewide title / description here on purpose: every leaf route owns
      // its own, and a root-level default was emitted alongside them.
      
      
      { name: "theme-color", content: "#101B2E" },
      { property: "og:site_name", content: "Pal Stars" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      // No sitewide og:title / og:description / og:image here on purpose:
      // every route owns its own social preview, and a root-level default was
      // being emitted alongside them so shared links showed generic site copy
      // instead of the page's own title, description and image.
    ],
    links: [
      // The app stylesheet is no longer linked here: it is imported as route CSS
      // above so Start inlines it into the SSR HTML (no render-blocking request).

      // Small, dedicated icon files — the 512px source PNG is 324 KB and must
      // never be fetched just to paint a favicon or the 36px header logo.
      { rel: "icon", href: "/favicon-64.png", type: "image/png", sizes: "64x64" } as any,
      { rel: "apple-touch-icon", href: "/eidrolon-avatar-180.png", sizes: "180x180" } as any,
      // The header logo renders in the very first paint on every route.
      { rel: "preload", as: "image", href: "/eidrolon-avatar-72.webp", type: "image/webp", fetchPriority: "high" } as any,

      
      // Official card scans are the LCP element on card-heavy pages, so open the
      // connection during HTML parsing instead of after the first <img> is seen.
      { rel: "preconnect", href: "https://en.palworld-official-cardgame.com", crossOrigin: "anonymous" },
      { rel: "dns-prefetch", href: "https://en.palworld-official-cardgame.com" },
      // Official Pal artwork (portraits in the database, games and hero grids)
      // comes from the wiki CDN; opening that socket during HTML parsing takes a
      // full DNS + TLS round trip off the image dependency chain.
      { rel: "preconnect", href: "https://static.wikia.nocookie.net", crossOrigin: "anonymous" },
      { rel: "dns-prefetch", href: "https://static.wikia.nocookie.net" },
      // Analytics is async, non-blocking and injected after first interaction, so
      // it only warrants DNS resolution — Lighthouse flags more than four
      // preconnect origins, and the four above are the render-critical ones.
      { rel: "dns-prefetch", href: "https://www.googletagmanager.com" },
      { rel: "dns-prefetch", href: "https://www.google-analytics.com" },



      // Fonts are self-hosted (see src/styles.css) with `font-display: swap` and
      // metric-matched local fallbacks, so text paints immediately in a system
      // font and swaps without reflow. They are deliberately NOT preloaded:
      // 70 KB of high-priority font bytes ahead of first paint pushed mobile FCP
      // from 0.8s to 3.4s in the throttled Lighthouse run.



    ],
    scripts: [
      // Google Analytics (G-CR7QGT89CB) still loads on every page, but the
      // gtag.js request is queued off the critical path: dataLayer/gtag exist
      // immediately (so nothing is lost), and the third-party script is injected
      // on the first interaction or when the browser goes idle. This keeps GA out
      // of the initial network dependency tree without dropping any hits.
      {
        children:
          "window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('config','G-CR7QGT89CB');(function(){var d=0;function l(){if(d)return;d=1;['pointerdown','keydown','scroll','touchstart'].forEach(function(e){window.removeEventListener(e,l)});var s=document.createElement('script');s.async=true;s.src='https://www.googletagmanager.com/gtag/js?id=G-CR7QGT89CB';document.head.appendChild(s);}['pointerdown','keydown','scroll','touchstart'].forEach(function(e){window.addEventListener(e,l,{once:true,passive:true})});function q(){if('requestIdleCallback'in window)requestIdleCallback(l,{timeout:10000});else setTimeout(l,9000);}if(document.readyState==='complete')q();else window.addEventListener('load',q,{once:true});})();",
      },

      {
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "WebSite",
        "@id": "https://palstars.com/#website",
        name: "Pal Stars",
        url: "https://palstars.com/",
        alternateName: ["Pal Stars: Palworld Wiki & Database"],
        description: "Pal Stars — the Palworld fan wiki & database.",
        inLanguage: "en",
        publisher: {
          "@type": "Organization",
          "@id": "https://palstars.com/#organization",
          name: "Pal Stars",
          url: "https://palstars.com/",
          logo: "https://palstars.com/eidrolon-avatar.png",
        },
        // NOTE: `speakable` is only valid on WebPage/Article types — it lives on
        // each route's WebPage node, not here on WebSite.

        potentialAction: {
          "@type": "SearchAction",
          target: {
            "@type": "EntryPoint",
            urlTemplate: "https://palstars.com/pals?q={search_term_string}",
          },
          "query-input": "required name=search_term_string",
        },

      }),
    }],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="en">
      <head><HeadContent /></head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  const [showChat, setShowChat] = useState(false);
  // Phones get no floating overlays at all: the cookie disclaimer, the free
  // booster-pack teaser and the AI chat launcher all covered real content on
  // small screens. Resolved after hydration so SSR markup stays identical.
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const sync = () => setIsMobile(mq.matches);
    sync();
    mq.addEventListener("change", sync);
    const t = setTimeout(() => setShowChat(true), 1200);
    return () => { mq.removeEventListener("change", sync); clearTimeout(t); };
  }, []);
  return (
    <QueryClientProvider client={queryClient}>
      <div className="min-h-screen flex flex-col">
        <SiteHeader />
        <main className="flex-1">
          <BackButton />
          <Breadcrumbs />
          <Outlet />
        </main>


        <SiteFooter />
        <ScrollPager />
        {showChat && <Suspense fallback={null}><AccessibilityControls /></Suspense>}
        {!isMobile && <Suspense fallback={null}><CookieBanner /></Suspense>}
        {/* Field Core Web Vitals (LCP/CLS/INP/TTFB) reported to GA per route. */}
        <WebVitals />



      </div>
    </QueryClientProvider>
  );
}
